import {
  DrawButton,
  LayerSwitcher,
  LayerTree,
  MapComponent,
  MapContext,
} from "@terrestris/react-geo";
import { Button, Col, Divider, Popover, Row, Space } from "antd";
import CommonOverlayComponent from "commons/CommonOverlayComponent";
import CommonCard from "commons/Layout/CommonCard";
import { AuthContext } from "context/auth";
import OlLayerTile from "ol/layer/Tile";
import OlMap from "ol/Map";
import { fromLonLat } from "ol/proj";
import OlSourceOsm from "ol/source/OSM";
import OlView from "ol/View";
import { getCenter } from "ol/extent";
import React, { FC, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DateNow, FormatDate } from "utils/helpers/date_helpers";
import transactWFS from "utils/helpers/WFSTransactions";
import { AirTreeProjectFormFields } from "./components/projectFormFields";
import { v4 as uuidv4 } from "uuid";
import { geoFormatArea } from "utils/helpers/geo_helpers";
import ServiceGeoServer from "services/geoServer";
import { bbox as bboxStrategy } from "ol/loadingstrategy";
import VectorSource from "ol/source/Vector";
import GeoJSON from "ol/format/GeoJSON";
import VectorLayer from "ol/layer/Vector";
import { Fill, Stroke, Style } from "ol/style";
import * as turf from "@turf/turf";
import BingMaps from "ol/source/BingMaps";
import { useMemo } from "react";
import { EyeOutlined } from "@ant-design/icons";
import ServiceAirTree from "services/airTree";

const AirTreeCreateProject: FC = () => {
  let navigate = useNavigate();
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);
  const [currentMainArea, setCurrentMainArea] = useState(null);
  const [geoFeature, setGeoFeature] = useState(null);
  const [map, setMap] = useState(null);
  const { currentUser } = useContext(AuthContext);
  const [regionsVector, setRegionsVector] = useState(null);
  const [intersectedRegion, setIntersectedRegion] = useState(null);
  const [simulationDateRange, setSimulationDateRange] = useState(null);

  const latlon = [12.4963655, 41.9027835];

  const center = fromLonLat(latlon);

  const geoServerUrl = process?.env?.REACT_APP_GEOSERVER_URL;

  useEffect(() => {
    // Get Simulation Date Range
    ServiceAirTree.getRangeSimulationDate()
      .then((res) => {
        setSimulationDateRange(res);
      })
      .catch((e) => {
        console.log("Error: ", e);
      });

    // Get Regions Vector
    ServiceGeoServer.getItalianRegionsShape().then((wfsRegions) => {
      const vectorSource = new VectorSource({
        features: new GeoJSON().readFeatures(wfsRegions),
        strategy: bboxStrategy,
      });

      const vectorProject = new VectorLayer({
        source: vectorSource,
        // @ts-ignore
        name: "Regioni",
        style: new Style({
          fill: new Fill({
            color: "rgba(94, 176, 44, 0.1)",
          }),
          stroke: new Stroke({
            color: "rgba(94, 176, 44, 1.0)",
            width: 2,
          }),
        }),
      });
      setRegionsVector(vectorProject);
    });
  }, []);

  useEffect(() => {
    if (regionsVector) {
      setMap(
        new OlMap({
          layers: [
            new OlLayerTile({
              // @ts-ignore
              name: "OSM",
              source: new OlSourceOsm(),
            }),
            new OlLayerTile({
              // @ts-ignore
              name: "Satellite",
              source: new BingMaps({
                key: "AgvK-EavqnKlSbi2mfp_nKxT5RpmBZLr4N9k1ChJeW1v8ePqaUEUSy2WYDllrYRH",
                imagerySet: "Aerial",
              }),
            }),
            regionsVector,
          ],
          view: new OlView({
            //projection: 'EPSG:4326',
            center: center,
            zoom: 7,
          }),
        })
      );
    }
  }, [regionsVector]);

  if (!map) {
    return null;
  }

  const setProjectArea = (feature) => {
    setCurrentMainArea(feature.getGeometry());
    setGeoFeature(feature);

    let turfFeat: any = new GeoJSON().writeFeatureObject(feature);
    let centroid = turf.centroid(turfFeat);
    let xc = centroid.geometry.coordinates[0];
    let yc = centroid.geometry.coordinates[1];

    // Get Intersection Region Name
    ServiceGeoServer.getItalianRegionsShapeIntersect(xc, yc).then(
      (response) => {
        setIntersectedRegion(response?.features?.[0]?.properties);
      }
    );

    setDrawerVisible(true);
  };

  // Submit form
  const onSubmit = async (values: any) => {
    // Get centroid lat e lon
    let mainFeature = geoFeature.clone();
    let polygonCenter = getCenter(
      mainFeature
        ?.getGeometry()
        ?.transform("EPSG:3857", "EPSG:4326")
        .getExtent()
    );
    let area = geoFormatArea(geoFeature.getGeometry(), "km");

    // Set properties
    let properties = {};
    properties = { ...values };
    properties["uuid"] = uuidv4();
    properties["created_by"] = currentUser?.id;
    properties["updated_by"] = currentUser?.id;
    properties["simulation_date_start"] = FormatDate(
      values?.simulation_date_start,
      "YYYY-MM-01"
    );
    properties["simulation_date_end"] = FormatDate(
      values?.simulation_date_end,
      "YYYY-MM-01"
    );
    properties["created_at"] = DateNow("X");
    properties["updated_at"] = DateNow("X");
    properties["centroid_lat"] = polygonCenter?.[1];
    properties["centroid_lng"] = polygonCenter?.[0];
    properties["area"] = area;
    properties["status"] = 0;
    properties["id_region"] = intersectedRegion?.COD_REG;
    properties["irrigation"] = values?.irrigation ? values?.irrigation : false;
    properties["note"] = values?.note ? values?.note : "";
    properties["soil_texture"] = values?.soil_texture
      ? values?.soil_texture
      : 0;
    properties["type"] = values?.type ? values?.type : 0;

    // Add properties to feature
    geoFeature.setProperties(properties);

    let writeParams = {
      mode: "insert",
      features: [geoFeature],
      layerCompleteName: "airtree:airtree_project",
      serverUrl: geoServerUrl,
      callback: function (res) {
        let rid =
          res.documentElement.lastChild.firstChild.firstChild.getAttribute(
            "rid"
          );
        let projectId = rid.split(".")[1];
        navigate(`/airtree-modify/${projectId}`, { replace: true });
      },
    };
    transactWFS(writeParams);
  };

  return (
    <CommonCard title={<h2>AirTree - Crea progetto</h2>}>
      <h3>
        Disegna l'area di pertinenza ed inserisci i dati di progetto che ti
        verranno richiesti, al termine delle operazioni procedi con il
        salvataggio della scheda ed inizia a creare il tuo progetto
      </h3>

      <p>L’area di interesse viene finalizzata con un doppio click</p>

      <Divider></Divider>
      <Row gutter={16}>
        <Col xs={24} sm={24} md={24} lg={24} xl={24}>
          {map && (
            <MapContext.Provider value={map}>
              <Row>
                <Col xs={24} sm={24} md={24} lg={24} xl={16}>
                  <Space>
                    <DrawButton
                      name="drawPolygon"
                      drawType="Polygon"
                      onDrawEnd={({ feature }) => setProjectArea(feature)}
                    >
                      Disegna Area Principale
                    </DrawButton>
                  </Space>
                </Col>
                <Col
                  xs={24}
                  sm={24}
                  md={24}
                  lg={24}
                  xl={24}
                  style={{ paddingTop: 20 }}
                >
                  <Popover
                    content={
                      <LayerTree
                        map={map}
                        filterFunction={(layer) => {
                          return (
                            layer.get("name") !== "OSM" &&
                            layer.get("name") !== "react-geo_digitize" &&
                            layer.get("id") !== "intersect"
                          );
                        }}
                      />
                    }
                    title="Selezione layers"
                    trigger="click"
                  >
                    <Button
                      style={{
                        position: "absolute",
                        top: "40px",
                        left: "50px",
                        zIndex: 2,
                      }}
                      type="default"
                      shape="circle"
                      icon={<EyeOutlined />}
                    />
                  </Popover>
                  <MapComponent
                    map={map}
                    style={{
                      height: "80vh",
                    }}
                  />
                </Col>
              </Row>
            </MapContext.Provider>
          )}
        </Col>
      </Row>

      <CommonOverlayComponent
        title={`Scheda progetto - territorio regione ${intersectedRegion?.DEN_REG}`}
        onSubmit={onSubmit}
        type="modal"
        destroyOnClose={true}
        //permission={DisabledFields}
        visible={drawerVisible}
        width={"60%"}
        okText="Conferma e Salva"
        onCancel={() => setDrawerVisible(false)}
        formsOpts={{
          debug: false,
          fields: AirTreeProjectFormFields({ simulationDateRange }),
          //ref: Form,
          hideSubmit: true,
          initialValues: currentRecord || undefined,
        }}
      />
    </CommonCard>
  );
};

export default AirTreeCreateProject;
